/**
 * Collection Filter Component - AJAX Tag Filtering with Sticky Filters
 * 
 * Works on ALL collections
 * - Handles filtering products by tags without page reload
 * - Supports custom sorting based on a "sort collection" order
 * - Includes sticky filter functionality (winner from A/B test)
 * - Auto-positions facets wrapper within filter component
 */

/**
 * Utility: Wait for an element to appear in the DOM
 */
const waitForElement = (selector, callback) => {
  const interval = setInterval(() => {
    const el = document.querySelector(selector);
    if (el) {
      clearInterval(interval);
      callback(el);
    }
  }, 200);
};

/**
 * Update navbar collection links to insert /shop-all
 * (Currently not actively used but preserved for future use)
 */
const updateNavbarCollectionLinks = () => {
  const anchors = document.querySelectorAll(
    '.shopify-section-group-header-group a[href]'
  );

  anchors.forEach((a) => {
    const href = a.getAttribute('href');
    if (!href) return;

    // Skip external links, anchors, mailto, tel, etc.
    if (!href.startsWith('/')) return;

    // Only target paths that start with /collections
    if (!href.startsWith('/collections')) return;

    // Avoid duplicating /shop-all
    if (href.startsWith('/collections/shop-all')) return;

    const [pathAndQuery, hash] = href.split('#');
    const [path, query] = pathAndQuery.split('?');

    // Insert /shop-all right after /collections, preserving the rest of the path
    const newPath = path.replace(
      /^\/collections(\/|$)/,
      '/collections/shop-all$1'
    );

    let newHref = newPath;
    if (query) newHref += `?${query}`;
    if (hash) newHref += `#${hash}`;

    a.setAttribute('href', newHref);
  });
};

class ABTagFilterAjax {
  constructor(container) {
    this.container = container;
    this.buttons = container.querySelectorAll('.ab-tag-filter-button');
    this.productGrid = document.getElementById('product-grid');
    this.productGridContainer = document.getElementById('ProductGridContainer');
    this.currentRequest = null;
    this.loadingButton = null;
    
    // Get the outer wrapper for sticky class (parent of container)
    this.filterWrapper = container.closest('.ab-collection-filter-ajax');
    
    // Get the base collection URL for "show all" functionality
    this.collectionUrl = container.dataset.collectionUrl || '/collections/shop-all';
    
    // Parse the tag-to-collection mapping from the data attribute
    const mapJson = container.dataset.tagCollectionMap;
    this.tagCollectionMap = mapJson ? JSON.parse(mapJson) : {};
    
    // Parse the reverse collection-to-tag mapping from the data attribute
    const reverseMapJson = container.dataset.collectionTagMap;
    this.collectionTagMap = reverseMapJson ? JSON.parse(reverseMapJson) : {};
    
    // Cache for collection product orders
    this.collectionOrderCache = {};

    // Sticky filter state
    this.isSticky = false;
    this.scrollTimeout = null;
    this.sentinel = null;
    this.mutationObserver = null;
    this.rafId = null;

    this.init();
  }

  init() {
    // Check URL on page load and apply active state to matching filter
    this.setActiveFromURL();
    
    this.buttons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFilterClick(e));
    });
    
    // Initialize sticky filters
    this.initStickyFilters();
    
    // Ensure facets wrapper stays inside the custom wrapper
    this.ensureFacetPosition();
    
    // Observe mutations in the collection section
    this.observeCollectionSection();
  }

  /**
   * Move the active filter button to the first position
   */
  moveActiveToFirst() {
    const activeButton = Array.from(this.buttons).find(btn => btn.classList.contains('active'));
    
    if (activeButton) {
      const parentItem = activeButton.closest('.ab-tag-filter-item');
      const filterList = parentItem?.parentElement;
      const title = filterList?.querySelector('.ab-filter-title');
      
      if (parentItem && filterList) {
        // Move active item to first position (after title if exists)
        if (title) {
          title.after(parentItem);
        } else {
          filterList.prepend(parentItem);
        }
      }
    }
  }

  /**
   * Initialize sticky filters functionality
   */
  initStickyFilters() {
    const STICK_THRESHOLD = 10;
    const UNSTICK_THRESHOLD = 50;

    // Create sentinel (spacer) to avoid layout shift when fixed
    this.sentinel = this.filterWrapper.previousElementSibling;

    if (!this.sentinel || this.sentinel.getAttribute('data-sticky-sentinel') !== 'true') {
      this.sentinel = document.createElement('div');
      this.sentinel.setAttribute('data-sticky-sentinel', 'true');
      this.filterWrapper.parentNode.insertBefore(this.sentinel, this.filterWrapper);
    }

    // Ensure sentinel has a stable baseline style
    this.sentinel.style.display = 'block';
    this.sentinel.style.height = '0px';

    // Check sticky state
    const checkSticky = () => {
      const filterTop = this.filterWrapper.getBoundingClientRect().top;
      const sentinelTop = this.sentinel.getBoundingClientRect().top;
      
      // Stick when filter reaches top
      if (!this.isSticky && filterTop <= STICK_THRESHOLD) {
        this.setSticky(true);
      }
      
      // Unstick when scrolling back up (use sentinel position as reference)
      if (this.isSticky) {
        if (sentinelTop > UNSTICK_THRESHOLD) {
          this.setSticky(false);
        }
      }
    };

    // Throttled scroll handler
    const handleScroll = () => {
      if (this.scrollTimeout) return;
      this.scrollTimeout = setTimeout(() => {
        checkSticky();
        this.scrollTimeout = null;
      }, 50);
    };

    checkSticky();
    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  /**
   * Set sticky state
   */
  setSticky(newStickyState) {
    if (this.isSticky === newStickyState) return;
    this.isSticky = newStickyState;

    if (newStickyState) {
      const height = this.filterWrapper.getBoundingClientRect().height;
      this.filterWrapper.classList.add('sticky-filters');
      this.sentinel.style.height = `${height}px`;
    } else {
      this.filterWrapper.classList.remove('sticky-filters');
      this.sentinel.style.height = '0px';
    }
  }

  /**
   * Ensure facets wrapper stays inside the custom wrapper
   */
  ensureFacetPosition() {
      const currentFilterWrapper = document.querySelector(
        '.ab-collection-filter-ajax'
      );
      const currentFacetsWrapper = document.querySelector('aside.facets-wrapper');
      if (!currentFilterWrapper || !currentFacetsWrapper) return;

      if (!currentFilterWrapper.contains(currentFacetsWrapper)) {
        currentFilterWrapper.prepend(currentFacetsWrapper);
      }
  }

  /**
   * Observe mutations in the collection section
   */
  observeCollectionSection() {
    const collectionSection = document.querySelector('.section-collection-product');
    if (!collectionSection) return;

    this.mutationObserver = new MutationObserver(() => {
      if (this.rafId) cancelAnimationFrame(this.rafId);

      this.rafId = requestAnimationFrame(() => {
        this.ensureFacetPosition();

        // If sticky is active, refresh sentinel height in case layout changed
        if (this.isSticky && this.sentinel) {
          this.sentinel.style.height = `${this.filterWrapper.getBoundingClientRect().height}px`;
        }

        this.rafId = null;
      });
    });

    this.mutationObserver.observe(collectionSection, { childList: true, subtree: true });
  }

  /**
   * Check the current URL and apply active state to matching filter button
   */
  setActiveFromURL() {
    const currentPath = window.location.pathname;
    
    // First, remove all active states
    this.buttons.forEach(button => {
      button.classList.remove('active');
      button.setAttribute('aria-pressed', 'false');
    });
    
    // Extract collection and optional tag segments from the path.
    // Supports:
    // - /collections/{collection}
    // - /collections/{collection}/{tag}
    const pathMatch = currentPath.match(/^\/collections\/([^\/?]+)(?:\/([^\/?]+))?/);
    const currentCollectionHandle = pathMatch ? pathMatch[1] : null;
    const currentTagHandle = pathMatch ? pathMatch[2] : null;

    // Priority 1: for tagged URLs like /collections/shop-all/classroom-kits,
    // use the explicit tag segment from the path.
    if (currentTagHandle) {
      this.buttons.forEach(button => {
        if (button.dataset.tagHandle === currentTagHandle) {
          button.classList.add('active');
          button.setAttribute('aria-pressed', 'true');
        }
      });
      return;
    }
    
    // Priority 2: map plain collection URLs to a tag filter.
    // Example: /collections/phonics-class-kits -> classroom-kits
    let targetTagHandle = null;
    if (currentCollectionHandle && this.collectionTagMap[currentCollectionHandle]) {
      targetTagHandle = this.collectionTagMap[currentCollectionHandle];
    }
    
    // If we have a mapped collection, ONLY activate that button
    if (targetTagHandle) {
      this.buttons.forEach(button => {
        if (button.dataset.tagHandle === targetTagHandle) {
          button.classList.add('active');
          button.setAttribute('aria-pressed', 'true');
        }
      });
      return;
    }
    
    // Priority 3: fallback matching for uncommon URL formats
    this.buttons.forEach(button => {
      const tagUrl = button.dataset.tagUrl;
      const tagHandle = button.dataset.tagHandle;
      
      if (!tagUrl) return;
      
      // Exact path match (for tagged collections on shop-all)
      const tagUrlPath = tagUrl.split('?')[0];
      if (currentPath === tagUrlPath) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
        return;
      }
      
      // Path ends with tag handle (for tagged collections)
      if (currentPath.endsWith(`/${tagHandle}`)) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
        return;
      }
    });
  }

  /**
   * Get the sort collection handle for a given tag handle
   */
  getSortCollectionForTag(tagHandle) {
    return this.tagCollectionMap[tagHandle] || null;
  }

  /**
   * Fetch the product order from a collection
   * Returns an array of product IDs in the collection's order
   */
  async fetchCollectionOrder(collectionHandle) {
    if (!collectionHandle) return null;
    
    // Check cache first
    if (this.collectionOrderCache[collectionHandle]) {
      return this.collectionOrderCache[collectionHandle];
    }

    try {
      // Fetch all products from the sort collection (up to 250)
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);
      if (!response.ok) return null;
      
      const data = await response.json();
      const productOrder = data.products.map(p => p.id);
      
      // Cache the result
      this.collectionOrderCache[collectionHandle] = productOrder;
      
      return productOrder;
    } catch (error) {
      console.error('Error fetching collection order:', error);
      return null;
    }
  }

  /**
   * Fetch best-selling order for a tagged collection
   * Returns an array of product IDs sorted by best-selling
   */
  async fetchBestSellingOrder(tagUrl) {
    const cacheKey = `bestselling:${tagUrl}`;
    
    // Check cache first
    if (this.collectionOrderCache[cacheKey]) {
      return this.collectionOrderCache[cacheKey];
    }

    try {
      // Fetch products sorted by best-selling
      const response = await fetch(`${tagUrl}/products.json?limit=250&sort_by=best-selling`);
      if (!response.ok) return null;
      
      const data = await response.json();
      const productOrder = data.products.map(p => p.id);
      
      // Cache the result
      this.collectionOrderCache[cacheKey] = productOrder;
      
      return productOrder;
    } catch (error) {
      console.error('Error fetching best-selling order:', error);
      return null;
    }
  }

  /**
   * Sort the product grid based on collection order
   * Products not in the collection are sorted by best-selling and placed at the end
   */
  sortProductGrid(collectionOrder, bestSellingOrder = null) {
    if (!collectionOrder || !this.productGrid) return;

    const productCards = Array.from(this.productGrid.querySelectorAll('.grid__item'));
    if (productCards.length === 0) return;

    // Create a map of product ID to its position in the sort collection
    const collectionOrderMap = new Map();
    collectionOrder.forEach((id, index) => {
      collectionOrderMap.set(id, index);
    });

    // Create a map for best-selling order (for products not in collection)
    const bestSellingMap = new Map();
    if (bestSellingOrder) {
      bestSellingOrder.forEach((id, index) => {
        bestSellingMap.set(id, index);
      });
    }

    // Sort the cards
    productCards.sort((a, b) => {
      const cardA = a.querySelector('[data-product-id]');
      const cardB = b.querySelector('[data-product-id]');
      
      const idA = cardA ? parseInt(cardA.dataset.productId, 10) : null;
      const idB = cardB ? parseInt(cardB.dataset.productId, 10) : null;

      const inCollectionA = idA && collectionOrderMap.has(idA);
      const inCollectionB = idB && collectionOrderMap.has(idB);

      // Both in collection: sort by collection order
      if (inCollectionA && inCollectionB) {
        return collectionOrderMap.get(idA) - collectionOrderMap.get(idB);
      }
      
      // Only A in collection: A comes first
      if (inCollectionA && !inCollectionB) {
        return -1;
      }
      
      // Only B in collection: B comes first
      if (!inCollectionA && inCollectionB) {
        return 1;
      }
      
      // Neither in collection: sort by best-selling order
      if (bestSellingOrder) {
        const posA = idA && bestSellingMap.has(idA) ? bestSellingMap.get(idA) : Infinity;
        const posB = idB && bestSellingMap.has(idB) ? bestSellingMap.get(idB) : Infinity;
        return posA - posB;
      }
      
      // Fallback: maintain original order
      return 0;
    });

    // Re-append in sorted order
    productCards.forEach(card => {
      this.productGrid.appendChild(card);
    });
  }

  async handleFilterClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const isActive = button.classList.contains('active');
    
    // If clicking an active button, show all products (deselect filter)
    if (isActive) {
      return this.showAllProducts(button);
    }
    
    const tagHandle = button.dataset.tagHandle;
    let tagUrl = button.dataset.tagUrl;
    const sortOrder = button.dataset.sortOrder || '';
    
    // Look up the sort collection from our mapping
    const sortCollection = this.getSortCollectionForTag(tagHandle);

    // Cancel any pending requests
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Update active state
    this.updateActiveButton(button);

    // Show loading state on the clicked button
    this.showLoading(button);

    try {
      // Start fetching collection order and best-selling order in parallel if needed
      const collectionOrderPromise = sortCollection 
        ? this.fetchCollectionOrder(sortCollection) 
        : Promise.resolve(null);
      
      // Also fetch best-selling order for the tagged collection (for products not in sort collection)
      const bestSellingPromise = sortCollection 
        ? this.fetchBestSellingOrder(tagUrl.split('?')[0])
        : Promise.resolve(null);

      // Preserve existing URL parameters (like ?view=v2 or ?sort_by=...)
      const currentParams = new URLSearchParams(window.location.search);
      
      // Build base URL without any query params
      const baseUrl = tagUrl.split('?')[0];
      
      // Create new params object starting with current params
      const params = new URLSearchParams(currentParams);

      // Reset pagination when switching categories/tags.
      // Carrying page=2+ to a smaller filtered set can return an empty grid.
      params.delete('page');
      
      // Ensure view=v2 is always set for the correct template
      params.set('view', 'v2');
      
      // Add section_id for AJAX - use v2 section when view=v2
      params.set('section_id', 'main-collection-product-grid-v2');
      
      // Preserve existing sort_by from URL, or use button's default sortOrder as fallback
      // Note: If we have a sortCollection, client-side sorting will be applied after fetch,
      // but we still need to include sort_by in the request for proper server-side filtering
      const existingSortBy = currentParams.get('sort_by');
      // 'manual' is Shopify's default sort (collection order), treat it as no sort selected
      const hasUserSelectedSort = existingSortBy && existingSortBy !== 'manual';
      if (!hasUserSelectedSort && sortOrder) {
        // No sort currently applied (or manual), use button's default
        params.set('sort_by', sortOrder);
      }
      // If user has selected a specific sort, it's already in params from currentParams
      
      // Build the URL with preserved parameters
      const url = `${baseUrl}?${params.toString()}`;
      
      this.currentRequest = new AbortController();
      const response = await fetch(url, {
        signal: this.currentRequest.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract the new product grid
      const newProductGrid = doc.getElementById('product-grid');
      const newProductGridContainer = doc.getElementById('ProductGridContainer');
      
      // Extract the facet filters
      const newFacetFilters = doc.getElementById('main-collection-filters');
      const currentFacetFilters = document.getElementById('main-collection-filters');

      if (newProductGrid && this.productGrid) {
        // Replace the product grid content
        this.productGrid.innerHTML = newProductGrid.innerHTML;
        
        // Update the entire container if needed (for pagination, etc.)
        if (newProductGridContainer && this.productGridContainer) {
          this.productGridContainer.innerHTML = newProductGridContainer.innerHTML;
          // Re-acquire the product grid reference after replacing container
          this.productGrid = document.getElementById('product-grid');
        }

        // Update facet filters if they exist
        if (newFacetFilters && currentFacetFilters) {
          currentFacetFilters.innerHTML = newFacetFilters.innerHTML;
          
          // Re-initialize facets functionality
          if (typeof window.FacetFiltersForm !== 'undefined') {
            document.querySelectorAll('facet-filters-form').forEach(form => {
              if (form.debouncedOnSubmit) {
                form.debouncedOnSubmit = form.onSubmitHandler.bind(form);
              }
            });
          }
        }

        // Try to perform post-update operations (sorting, URL update, re-init)
        // If these fail, we log the error but DO NOT trigger the fallback refresh
        // because the content has already been successfully updated.
        try {
          // Wait for collection order and best-selling order, then apply client-side sorting
          // BUT only if user hasn't selected a specific sort option
          const [collectionOrder, bestSellingOrder] = await Promise.all([
            collectionOrderPromise,
            bestSellingPromise
          ]);
          
          // Only apply custom collection sorting if:
          // 1. We have a collection order AND
          // 2. User hasn't selected a specific sort (or sort is 'manual' which is default)
          const userSelectedSort = currentParams.get('sort_by');
          const hasUserSelectedSort = userSelectedSort && userSelectedSort !== 'manual';
          if (collectionOrder && !hasUserSelectedSort) {
            this.sortProductGrid(collectionOrder, bestSellingOrder);
          }

          // Update browser URL to reflect the tag filter
          // This ensures sorting and other facet filters preserve the tag
          const baseTagUrl = tagUrl.split('?')[0];
          // Use the params we built for the AJAX request, but remove section_id
          const urlParams = new URLSearchParams(params.toString());
          urlParams.delete('section_id');
          const newUrl = urlParams.toString() ? `${baseTagUrl}?${urlParams.toString()}` : baseTagUrl;
          history.pushState({ searchParams: urlParams.toString() }, '', newUrl);

          // Re-initialize any quick add buttons or other components
          this.reinitializeComponents();
        } catch (postUpdateError) {
          console.error('Error in post-update operations:', postUpdateError);
        }
      }

      this.hideLoading();

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
      } else {
        console.error('Error fetching tag filter:', error);
        this.hideLoading();
        // Show error message to user instead of forcing page refresh
        this.showError('Unable to load products. Please try again.');
      }
    }
  }

  /**
   * Show all products (deselect current filter)
   */
  async showAllProducts(currentButton) {
    // Cancel any pending requests
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Remove active state from all buttons
    this.buttons.forEach(button => {
      button.classList.remove('active');
      button.setAttribute('aria-pressed', 'false');
    });

    // Show loading state on the clicked button
    this.showLoading(currentButton);

    try {
      // Preserve existing URL parameters
      const currentParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams(currentParams);

      // Reset pagination when returning to "all products"
      params.delete('page');
      
      // Ensure view=v2 is always set for the correct template
      params.set('view', 'v2');
      params.set('section_id', 'main-collection-product-grid-v2');
      
      const url = `${this.collectionUrl}?${params.toString()}`;
      
      this.currentRequest = new AbortController();
      const response = await fetch(url, {
        signal: this.currentRequest.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newProductGrid = doc.getElementById('product-grid');
      const newProductGridContainer = doc.getElementById('ProductGridContainer');
      const newFacetFilters = doc.getElementById('main-collection-filters');
      const currentFacetFilters = document.getElementById('main-collection-filters');

      if (newProductGrid && this.productGrid) {
        this.productGrid.innerHTML = newProductGrid.innerHTML;
        
        if (newProductGridContainer && this.productGridContainer) {
          this.productGridContainer.innerHTML = newProductGridContainer.innerHTML;
          this.productGrid = document.getElementById('product-grid');
        }

        if (newFacetFilters && currentFacetFilters) {
          currentFacetFilters.innerHTML = newFacetFilters.innerHTML;
        }

        try {
          // Update browser URL
          const urlParams = new URLSearchParams(params.toString());
          urlParams.delete('section_id');
          const newUrl = urlParams.toString() ? `${this.collectionUrl}?${urlParams.toString()}` : this.collectionUrl;
          history.pushState({ searchParams: urlParams.toString() }, '', newUrl);

          this.reinitializeComponents();
        } catch (postError) {
          console.error('Error in post-update operations (showAll):', postError);
        }
      }

      this.hideLoading();

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error showing all products:', error);
        this.hideLoading();
        this.showError('Unable to load products. Please try again.');
      }
    }
  }

  updateActiveButton(activeButton) {
    this.buttons.forEach(button => {
      button.classList.remove('active');
      button.setAttribute('aria-pressed', 'false');
    });

    activeButton.classList.add('active');
    activeButton.setAttribute('aria-pressed', 'true');
    
    // Move the newly active button to first position
    //this.moveActiveToFirst();
  }

  showLoading(button) {
    this.loadingButton = button;
    if (button) {
      button.classList.add('loading');
    }
    if (this.productGridContainer) {
      this.productGridContainer.style.opacity = '0.5';
      this.productGridContainer.style.pointerEvents = 'none';
    }
  }

  hideLoading() {
    if (this.loadingButton) {
      this.loadingButton.classList.remove('loading');
      this.loadingButton = null;
    }
    if (this.productGridContainer) {
      this.productGridContainer.style.opacity = '1';
      this.productGridContainer.style.pointerEvents = 'auto';
    }
  }

  scrollToProducts() {
    const scrollTarget = this.productGrid || this.productGridContainer;
    if (scrollTarget) {
      const offset = 100; // Offset for fixed header
      const elementPosition = scrollTarget.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  reinitializeComponents() {
    // Re-initialize quick add buttons if they exist
    if (typeof window.initializeQuickAdd === 'function') {
      window.initializeQuickAdd();
    }

    // Ensure facets are in the correct position after update
    this.ensureFacetPosition();

    // Trigger a custom event that other scripts can listen to
    document.dispatchEvent(new CustomEvent('ab-tag-filter:updated', {
      detail: { productGrid: this.productGrid }
    }));
  }

  showError(message) {
    // Remove any existing error messages
    const existingError = this.container.querySelector('.ab-filter-error-message');
    if (existingError) {
      existingError.remove();
    }

    // Create and show error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'ab-filter-error-message';
    errorDiv.textContent = message;
    errorDiv.style.cssText = 'color: #c41e3a; padding: 1rem; text-align: center; font-size: 14px;';
    
    this.container.insertBefore(errorDiv, this.container.firstChild);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Wait for the collection section and filter component to be fully rendered
  waitForElement('.section-collection-product', () => {
    waitForElement('[data-ab-tag-filter-component]', (filterContainer) => {
      new ABTagFilterAjax(filterContainer);
    });
  });
});